// Pilot kavram detay sayfaları için seçilmiş terimler.
// Her terim için TR id (= TR slug) ve EN slug eşleşmesi.
//
// Detay sayfaları yalnızca bu listedeki terimler için açıktır;
// diğer kavramlar şimdilik hub sayfasında (accordion) kalır.
// SEO sonuçları izlendikten sonra liste genişletilebilir.

export type PilotTerm = {
  id: string; // TR slug — Entry.id ile birebir
  enSlug: string; // EN slug
};

export const PILOT_TERMS: PilotTerm[] = [
  { id: "jevons-paradoksu", enSlug: "jevons-paradox" },
  { id: "ayrisma-tezi", enSlug: "decoupling-thesis" },
  { id: "rebound-etkisi", enSlug: "rebound-effect" },
  { id: "washing-ekonomisi", enSlug: "washing-economy" },
  { id: "algoritmik-teslimiyet", enSlug: "algorithmic-surrender" },
  { id: "yalnizlik-ekonomisi", enSlug: "loneliness-economy" },
  { id: "dikkat-madenciligi", enSlug: "attention-mining" },
  { id: "epistemik-silahlanma", enSlug: "epistemic-weaponization" },
  { id: "taahhut-borcu", enSlug: "commitment-debt" },
  { id: "ikinci-el-anlati", enSlug: "second-hand-story" },
];

const TR_TO_EN: Record<string, string> = Object.fromEntries(
  PILOT_TERMS.map((t) => [t.id, t.enSlug]),
);
const EN_TO_TR: Record<string, string> = Object.fromEntries(
  PILOT_TERMS.map((t) => [t.enSlug, t.id]),
);

function normalizeSlug(slug: string): string {
  return decodeURIComponent(slug)
    .trim()
    .toLocaleLowerCase("tr-TR")
    .replace(/ı/g, "i")
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ş/g, "s")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c")
    .replace(/\([^)]*\)/g, "")
    .replace(/&/g, " ve ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** Resolve any pilot slug (TR or EN) back to the canonical TR id. */
export function resolvePilotId(slug: string): string | null {
  const normalized = normalizeSlug(slug);
  if (TR_TO_EN[normalized]) return normalized;
  if (EN_TO_TR[normalized]) return EN_TO_TR[normalized];
  return null;
}

export function pilotSlug(id: string, lang: "en" | "tr"): string {
  return lang === "tr" ? id : TR_TO_EN[id] ?? id;
}

export function isPilot(id: string): boolean {
  return Boolean(TR_TO_EN[id]);
}

/**
 * TR pilot id -> canonical English concept slug on the English brand domain
 * (/concepts/<slug>). The English lexicon detail pages on the Turkish domain
 * are duplicates of these, so they canonicalise here and hreflang pairs the
 * TR /sozluk page with the EN /concepts page in both directions.
 */
const TR_TO_CONCEPT: Record<string, string> = {
  "jevons-paradoksu": "jevons-paradox",
  "ayrisma-tezi": "decoupling-thesis",
  "rebound-etkisi": "rebound-effect",
  "washing-ekonomisi": "washing-economy",
  "algoritmik-teslimiyet": "algorithmic-surrender",
  "yalnizlik-ekonomisi": "loneliness-economy",
  "dikkat-madenciligi": "attention-mining",
  "epistemik-silahlanma": "epistemic-weaponization",
  "taahhut-borcu": "commitment-debt",
};

const CONCEPT_TO_TR: Record<string, string> = Object.fromEntries(
  Object.entries(TR_TO_CONCEPT).map(([tr, en]) => [en, tr]),
);

/** English /concepts slug for a TR pilot id, when one exists. */
export function conceptSlugForPilot(id: string): string | undefined {
  return TR_TO_CONCEPT[id];
}

/** TR /sozluk slug for an English /concepts slug, when one exists. */
export function pilotIdForConcept(conceptSlug: string): string | undefined {
  return CONCEPT_TO_TR[conceptSlug];
}
